import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  TablePagination,
  Tooltip,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { formatDate } from '../utils/dateFormat';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, genericCopy, pagination, displayTimeZoneCopy } from '../utils/userFacingCopy';
import type { PagedResult } from '../types/pagedResult';

interface EmailLog {
  id: string;
  toEmail: string;
  toName: string;
  fromEmail: string;
  fromName: string;
  subject: string;
  htmlContent: string;
  plainTextContent?: string;
  sentAt: string;
  success: boolean;
  errorMessage?: string;
}

export default function EmailLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading, displayTimeZoneId } = useAuth();
  const isAdmin = user?.roles?.includes('Administrator') ?? false;
  // Check if we're viewing "My Emails" (personal view) vs "Email Logs" (admin audit view)
  // Admins have two views:
  //   1. "Email Logs" (audit view) - accessed via sidebar, shows all system emails
  //   2. "My Emails" (personal view) - accessed via dropdown, shows only their emails
  // If view param is explicitly set (e.g., ?view=my-emails), use it
  // Otherwise, default based on admin status: admins get 'all' (Email Logs), others get 'my-emails'
  const viewParam = searchParams.get('view');

  // Update URL if no view param and user has loaded (to ensure correct default)
  // This ensures admins default to 'all' (Email Logs audit view) when accessing from sidebar
  useEffect(() => {
    if (!authLoading && user && !viewParam) {
      const defaultView = isAdmin ? 'all' : 'my-emails';
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('view', defaultView);
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [authLoading, user, isAdmin, viewParam, searchParams, setSearchParams]);

  // Determine view mode: wait for auth to load, then use explicit param or default
  // For admins: 'all' = Email Logs (audit), 'my-emails' = My Emails (personal)
  // For non-admins: always 'my-emails' (they only have personal view)
  const viewMode = authLoading ? 'my-emails' : (viewParam || (isAdmin ? 'all' : 'my-emails'));
  const isPersonalView = viewMode === 'my-emails';
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pagination.auditLogDefaultPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [searchToEmail, setSearchToEmail] = useState('');
  const [searchSubject, setSearchSubject] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);

  const loadEmailLogs = useCallback(async () => {
    if (!user) {
      setError(genericCopy.signInRequired);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      params.append('page', (page + 1).toString());
      params.append('pageSize', rowsPerPage.toString());

      // In personal view, filter by user's email (even for admins)
      if (isPersonalView && user?.email) {
        params.append('toEmail', user.email);
      } else if (isAdmin && !isPersonalView && searchToEmail) {
        // Only allow searching by email if admin viewing all emails
        params.append('toEmail', searchToEmail);
      }

      if (searchSubject) {
        params.append('subject', searchSubject);
      }

      const response = await apiService.get<PagedResult<EmailLog>>(`${API_ENDPOINTS.EMAIL_LOGS.BASE}?${params.toString()}`);
      setEmailLogs(response.data.items);
      setTotalCount(response.data.totalCount);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotLoad('email logs')));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isPersonalView, page, rowsPerPage, searchToEmail, searchSubject, user]);

  useEffect(() => {
    loadEmailLogs();
  }, [loadEmailLogs]);

  const handleViewClick = (emailLog: EmailLog) => {
    setSelectedEmail(emailLog);
    setViewDialogOpen(true);
  };

  const handleViewClose = () => {
    setViewDialogOpen(false);
    setSelectedEmail(null);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EmailIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
                {isPersonalView ? 'Emails' : 'Email Logs'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          {isPersonalView
            ? 'View all emails that have been sent to you by the system, including notifications, reminders, and updates.'
            : 'This page shows all system-generated emails sent across the platform for audit and compliance purposes.'}{' '}
          {displayTimeZoneCopy.emailLogTimesHint}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {isAdmin && !isPersonalView && (
                <TextField
                  fullWidth
                  label="Search by recipient email"
                  value={searchToEmail}
                  onChange={(e) => setSearchToEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1 }}
                />
              )}
              <TextField
                fullWidth={isAdmin && !isPersonalView}
                label="Search by subject"
                value={searchSubject}
                onChange={(e) => setSearchSubject(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: isAdmin && !isPersonalView ? 1 : 'none' }}
              />
            </Box>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : emailLogs.length === 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Email Logs Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(searchToEmail || searchSubject)
                    ? 'Try adjusting your search criteria.'
                    : 'No emails have been sent yet.'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table key={displayTimeZoneId}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Sent At</strong></TableCell>
                    {isAdmin && !isPersonalView && <TableCell><strong>To</strong></TableCell>}
                    <TableCell><strong>From</strong></TableCell>
                    <TableCell><strong>Subject</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emailLogs.map((emailLog) => (
                    <TableRow key={emailLog.id} hover>
                      <TableCell>{formatDate(emailLog.sentAt, { includeTime: true })}</TableCell>
                      {isAdmin && !isPersonalView && (
                        <TableCell>
                          <Box>
                            <Typography variant="body2">{emailLog.toName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {emailLog.toEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{emailLog.fromName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {emailLog.fromEmail}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {emailLog.subject}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={emailLog.success ? <CheckCircleIcon /> : <ErrorIcon />}
                          label={emailLog.success ? 'Sent' : 'Failed'}
                          color={emailLog.success ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Email">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewClick(emailLog)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[...pagination.auditLogPageSizeOptions]}
            />
          </>
        )}

        {/* View Email Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={handleViewClose}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Typography component="span" variant="h6" fontWeight="600">
              Email Details
            </Typography>
            <IconButton onClick={handleViewClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedEmail && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Sent At</Typography>
                  <Typography variant="body1">{formatDate(selectedEmail.sentAt, { includeTime: true })}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">To</Typography>
                  <Typography variant="body1">
                    {selectedEmail.toName} ({selectedEmail.toEmail})
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">From</Typography>
                  <Typography variant="body1">
                    {selectedEmail.fromName} ({selectedEmail.fromEmail})
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Subject</Typography>
                  <Typography variant="body1">{selectedEmail.subject}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Chip
                    icon={selectedEmail.success ? <CheckCircleIcon /> : <ErrorIcon />}
                    label={selectedEmail.success ? 'Sent Successfully' : 'Failed'}
                    color={selectedEmail.success ? 'success' : 'error'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                {selectedEmail.errorMessage && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Error Message</Typography>
                    <Alert severity="error" sx={{ mt: 0.5 }}>
                      {selectedEmail.errorMessage}
                    </Alert>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    HTML Content
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      maxHeight: 400,
                      overflow: 'auto',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: selectedEmail.htmlContent }} />
                  </Box>
                </Box>
                {selectedEmail.plainTextContent && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Plain Text Content
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        maxHeight: 200,
                        overflow: 'auto',
                        bgcolor: 'grey.50',
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                      }}
                    >
                      {selectedEmail.plainTextContent}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1 }}>
            <Button onClick={handleViewClose} sx={{ textTransform: 'none' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AppLayout>
  );
}
